import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { AssignVinResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for assigning a VIN to an order
 */
export interface AssignVinInput {
	orderId: string;
	vin: string;
}

// VIN assignment is heavyweight (VIN validation + Firestore update + tracking + optimistic progress
// generation). The default 15s apiClient timeout is insufficient and causes false errors.
const ASSIGN_VIN_TIMEOUT_MS = 60_000;

/**
 * API function to assign a VIN to a purchased vehicle
 */
export const assignVin = async (
	servicesProviderId: string,
	{ orderId, vin }: AssignVinInput,
): Promise<AssignVinResponse> => {
	const response = await apiClient.post<AssignVinResponse>(
		ordersEndpoints.assignVin(servicesProviderId, orderId),
		{ vin },
		{ timeout: ASSIGN_VIN_TIMEOUT_MS },
	);
	return response.data;
};

/**
 * Hook for assigning a VIN to a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useAssignVin = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<AssignVinInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to assign VIN without an order ID or services provider ID.',
				);
			}
			return assignVin(servicesProviderId, { orderId, vin: input.vin });
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.ASSIGN_VIN);
		},
		onError: () => {
			if (!orderId || !servicesProviderId) return;

			// Invalidate progress + assignVin queries so the user sees fresh data on retry,
			// even if the backend actually succeeded (e.g. client timeout)
			queryClient.invalidateQueries({
				queryKey: queries.orders.detail(servicesProviderId, orderId)._ctx.progress
					.queryKey,
			});
		},
	});
};
